import React, { useMemo, useState } from 'react';
import { Field } from '@atlaskit/form';
import { defineMessages, useIntl } from '@atlassian/jira-intl';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { standardizeLocale } from '@atlassian/jira-issue-format-date/src/common/utils.tsx';
import { FormSelect } from '../../common/ui/form-select/FormSelect.tsx';

// https://atlassian.slack.com/archives/CFJ9P2RNJ/p1760940231668399?thread_ts=1760940215.757749&cid=CFJ9P2RNJ
// eslint-disable-next-line formatjs/blocklist-elements
export const messages = defineMessages({
	recurDayOfMonthLabel: {
		id: 'recur-work-item.fields.RecurMonthlyConfigField.recur-day-of-month-label',
		defaultMessage: 'Select day of month',
		description: 'Label for the select field used to configure a monthly recurring work item rule',
	},
	recurDayOfMonthSelectHeading: {
		id: 'recur-work-item.fields.RecurMonthlyConfigField.recur-day-of-month-select-heading',
		defaultMessage: 'On the',
		description: 'Label for the recur day of month select heading',
	},
	ordinalDayOfMonth: {
		id: 'recur-work-item.fields.RecurMonthlyConfigField.ordinal-day-of-month',
		// eslint-disable-next-line @atlassian/i18n/enforce-icu-format
		defaultMessage: '{dayOfMonth, selectordinal, one {#st} two {#nd} few {#rd} other{#th}}',
		description: 'Ordinal for day of month',
	},
	lastDayOfMonth: {
		id: 'recur-work-item.fields.RecurMonthlyConfigField.last-day-of-month',
		defaultMessage: 'Last day',
		description:
			'Last option in the recur day of month select field to indicate the last day of the month',
	},
	dayOfMonthSelected: {
		id: 'recur-work-item.fields.RecurMonthlyConfigField.day-of-month-selected',
		defaultMessage: 'On the {ordinalDayOfMonth}',
		description:
			'The label for each option in the recur day of month select field once the option has been selected',
	},
});

export const getRecurDayOfMonthOptionLabel = (
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
	dayOfMonth: number,
): string =>
	dayOfMonth > 0
		? formatMessage(messages.ordinalDayOfMonth, { dayOfMonth })
		: formatMessage(messages.lastDayOfMonth);

export type RecurMonthlyConfigValues = {
	recurDayOfMonth: number;
};

type RecurDayOfMonthOption = {
	label: string;
	value: number;
};

type RecurDayOfMonthOptionsWithHeading = {
	label: string;
	options: RecurDayOfMonthOption[];
};

type Props = {
	value?: RecurMonthlyConfigValues['recurDayOfMonth'];
};

const DEFAULT_RECUR_DAY_OF_MONTH_VALUE = 1;

export const RecurMonthlyConfigField = ({ value = DEFAULT_RECUR_DAY_OF_MONTH_VALUE }: Props) => {
	const { formatMessage } = useIntl();
	const locale = useLocale();
	const standardizedLocale = standardizeLocale(locale);
	const [dayOfMonth, setDayOfMonth] = useState<number>(value);

	const options: RecurDayOfMonthOption[] = useMemo(
		() =>
			Array.from({ length: 29 }, (_, i) => {
				// The user can select from days 1-28 and the last day (which we represent here as day -1)
				const adjDayOfMonth = i <= 27 ? i + 1 : -1;
				return {
					label: getRecurDayOfMonthOptionLabel(formatMessage, adjDayOfMonth),
					value: adjDayOfMonth,
				};
			}),
		[formatMessage],
	);

	const optionsOnceSelected: Map<number, RecurDayOfMonthOption> = useMemo(
		() =>
			new Map(
				options.map((option) => [
					option.value,
					{
						label: formatMessage(messages.dayOfMonthSelected, {
							ordinalDayOfMonth: option.label.toLocaleLowerCase(standardizedLocale),
						}),
						value: option.value,
					},
				]),
			),
		[formatMessage, options, standardizedLocale],
	);

	const optionsWithHeading: RecurDayOfMonthOptionsWithHeading[] = useMemo(() => {
		return [
			{
				label: formatMessage(messages.recurDayOfMonthSelectHeading).toLocaleUpperCase(
					standardizedLocale,
				),
				options,
			},
		];
	}, [formatMessage, options, standardizedLocale]);

	return (
		<Field<number> name="recurDayOfMonth" defaultValue={value}>
			{({ fieldProps: { onChange, ...restFieldProps } }) => (
				<FormSelect<RecurDayOfMonthOption>
					{...restFieldProps}
					options={optionsWithHeading}
					value={optionsOnceSelected.get(dayOfMonth)}
					onChange={(newValue) => {
						if (!newValue) return;
						onChange(newValue.value);
						setDayOfMonth(newValue.value);
					}}
					label={formatMessage(messages.recurDayOfMonthLabel)}
				/>
			)}
		</Field>
	);
};
